/**
 * Unified Support/Explanation Content Types
 *
 * This file is the SINGLE SOURCE OF TRUTH for all support content types.
 *
 * Hierarchy:
 * - `explanation`: Student-facing "Zo werkt het" — shown after incorrect answer or on-demand
 * - `hints`: Progressive HintStep[] — the modern spiekbriefje, shown in CheatSheetDrawer.tsx
 *
 * NOTE: The legacy `cheatSheet` object (title/body/rules) was removed in 2026-05.
 * The student-facing Spiekbriefje is now powered entirely by hints[] +
 * defaultExplanation (Glance card). See CHEATSHEET_HINT_SYSTEM_REDESIGN_2026.md.
 *
 * S-22 (Support RC audit, Ronde 3): Removed `SeedSupportDefinition` and the
 * legacy seed-override resolution path. No seed JSON or caller passed
 * seed-level support. Lossy duplicate fields (`ExplanationContent.steps`,
 * `ExplanationContent.hints: string[]`) were dropped — the lossless
 * `ResolvedSupport.hints: HintStep[]` is the single source of truth.
 *
 * S-17 (Support RC audit, Ronde 4): Removed `ResolvedSupport.steps`,
 * `ResolvedSupport.afterSummary`, `SupportCoverage.hasSteps` and
 * `SupportCoverage.hasAfterSummary`. They were forward-compat placeholders
 * since 2026-03 but never populated by any resolver and never read by any
 * consumer. YAGNI — re-add only when a tier-aware resolver actually needs
 * them and we have a concrete shape in mind. The deprecated `steps: string[]`
 * field had been `@deprecated v4.3` for over a year.
 */

import type { HintStep } from './hints';
export interface ExplanationContent {
  /** Display title, e.g. "Zo werkt het" */
  title: string;
  /** Main explanation body text */
  body: string;
  /** Reference to a visual aid type (optional) */
  visualRef?: string;
  /** Where this content originated */
  source: 'family' | 'seed' | 'override';
}
export interface ResolvedSupport {
  /** Primary explanation ("Zo werkt het") */
  explanation: ExplanationContent | null;
  /**
   * Progressive hints — full HintStep[] (title, content, visual, hintType).
   *
   * F-H1: This used to be `string[]`, which threw away the title and visual
   * fields and broke parity with `QuestionResolvedSupport.hints`. The admin
   * preview and any future consumer can now render hints lossless.
   */
  hints: HintStep[];
  /** Coverage report for diagnostics */
  coverage: SupportCoverage;
}
export interface SupportCoverage {
  hasExplanation: boolean;
  hasHints: boolean;
  /** Percentage of fields populated (0-100) */
  completeness: number;
  /** Sources used in resolution */
  sources: ('family' | 'seed' | 'override')[];
}

/**
 * S-7/S-9 (Support RC audit, Ronde 3): `FamilySupportDefinition` was removed.
 * After S-22 collapsed the seed-override path, the bridge function in
 * `supportResolver.ts` no longer needs to translate registry-shape into a
 * types.ts-shape; the resolver reads `FamilySupportEntry.defaultExplanation`
 * and `FamilySupportEntry.hints` straight from the registry. The naming gap
 * (`defaultExplanation` ↔ `howItWorks`) is gone with it.
 *
 * If a future tier-aware resolver needs an intermediate shape, define it
 * locally next to that resolver — don't reintroduce a public-API type.
 */

/**
 * Calculate support coverage completeness percentage.
 *
 * S-17 (Ronde 4): Field set reduced from 4 → 2. `hasSteps` and
 * `hasAfterSummary` were always `false` (no populating source existed),
 * which dragged the headline completeness number down to 50% even for
 * fully-authored families. The new ratio reflects what the resolver
 * actually produces: explanation + hints.
 */
export function calculateCoverage(coverage: Omit<SupportCoverage, 'completeness' | 'sources'>): number {
  const fields = [coverage.hasExplanation, coverage.hasHints];
  const filled = fields.filter(Boolean).length;
  return Math.round(filled / fields.length * 100);
}